import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import DataSetsItemButtons from 'components/DataSetsItemButtons/DataSetsItemButtons';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import TableModal from 'components/TableModal/TableModal';
import { MILLION, TABLE_HEADERS_PRODUCTS } from 'constants/dataSets';
import { SELECTED_ITEMS } from 'constants/formFields';
import { NUMBER_FORMATS } from 'constants/interfaces';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import formatNumber from 'utils/formatNumber';
import formattedData from 'utils/formattedData';
import formattedNumberWithComma from 'utils/formattedNumberWithComma';
import getMultiLabels from 'utils/getMultiLabels';

import type { IProductsItem } from './interfaces/IProductsItem';

import styles from './scss/ProductsItem.module.scss';

function ProductsItem({
  item, control, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, onEditRecord, onDeleteRecord, isActionsVisible,
  productCategoriesOptions,
}: IProductsItem): React.ReactElement {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const {
    id, name, code, criticalityRating, dateClosed, dateOpened,
    latestArchivation, suppliers, uniqueIdentifier, volumeQty,
    volumeQtyUnit, upstreamTier, relianceOnFreeNaturalResources, valueChainSection,
    relianceOnFreeSocialResources, circularityFromDownstreamWaste, profitCenterLinks,
    archived, productCategoryId,
  } = item;

  const hyphen = t('common.hyphen');
  const productCategoryLabel = productCategoriesOptions
    ?.find((category) => category.value === `${productCategoryId}`)
    ?.label || hyphen;

  const handleToggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const formattedProfitCenterLinks = profitCenterLinks.map((link) => ({
    ...link,
    startDate: link.startDate ? new Date(link.startDate).toLocaleDateString() : '',
    endDate: link.endDate ? new Date(link.endDate).toLocaleDateString() : '',
    totalCost: link.totalCost
      ? `${formattedNumberWithComma((Number(link.totalCost.split(' ')[0]) / MILLION).toFixed(2))} M ${companyCurrency}`
      : hyphen,
  }));

  return (
    <>
      <TableRow className={clsx(undefined, { [styles.archived]: archived })}>
        <TableCell>
          <div className={styles.staticCell}>
            {archived ? (
              <span>
                {name}
                {latestArchivation ? (
                  <InfoIcon
                    onClick={() => onArchiveModalInfoOpen(latestArchivation)}
                  />
                ) : null}
              </span>
            ) : null}

            {!archived && (
              isActionsVisible ? (
                <Controller
                  control={control}
                  render={({ field: { onChange, value, disabled } }) => (
                    <Checkbox
                      key={id}
                      name={`${SELECTED_ITEMS}-${id}`}
                      checked={!!value.includes(id)}
                      value={id}
                      disabled={disabled}
                      onChange={(event) => {
                        onChange(event.target.checked
                          ? [...value, id]
                          : value.filter((selectedItem) => selectedItem !== id));
                      }}
                      label={(<span>{name}</span>)}
                    />
                  )}
                  name={SELECTED_ITEMS}
                />
              ) : (<span>{name}</span>)
            )}
          </div>
        </TableCell>
        <TableCell>
          {code || hyphen}
        </TableCell>
        <TableCell>
          {uniqueIdentifier || hyphen}
        </TableCell>
        <TableCell className={styles.productCategoryCell}>
          {productCategoryLabel}
        </TableCell>
        <TableCell className={styles.suppliersCell}>
          <ul className={styles.suppliersList}>
            {suppliers.map(({ name: supplierName, uniqueIdentifier: supplierUniqueIdentifier }) => (
              <li key={`suppliers_${supplierUniqueIdentifier}`}>
                {`${supplierName} (${supplierUniqueIdentifier})`}
              </li>
            ))}
          </ul>
        </TableCell>
        <TableCell>
          {valueChainSection || hyphen}
        </TableCell>
        <TableCell>
          {upstreamTier || hyphen}
        </TableCell>
        <TableCell>
          {formattedData(dateOpened) || hyphen}
        </TableCell>
        <TableCell>
          {formattedData(dateClosed) || hyphen}
        </TableCell>
        <TableCell>
          {volumeQty ? `${formattedNumberWithComma(volumeQty)} ${volumeQtyUnit}` : hyphen}
        </TableCell>
        <TableCell>
          {relianceOnFreeNaturalResources ? getMultiLabels({ values: relianceOnFreeNaturalResources }) : hyphen}
        </TableCell>
        <TableCell>
          {relianceOnFreeSocialResources ? getMultiLabels({ values: relianceOnFreeSocialResources }) : hyphen}
        </TableCell>
        <TableCell>
          {criticalityRating || hyphen}
        </TableCell>
        <TableCell>
          {formatNumber(
            {
              value: circularityFromDownstreamWaste,
              type: NUMBER_FORMATS.percent,
            },
          )}
        </TableCell>
        <TableCell className={styles.profitCenterLinksCell}>
          <div className={styles.profitCenterLinksContainer}>
            <div className={styles.wrapper}>
              {profitCenterLinks.slice(0, 3).map((profitCenterLink) => (
                <div key={profitCenterLink.profitCenterId}>
                  {profitCenterLink.profitCenterId}
                </div>
              ))}
              {profitCenterLinks.length > 3 && (
              <div>...</div>
              )}
            </div>
            <InfoIcon
              onClick={handleToggleModal}
            />
          </div>
        </TableCell>

        {isActionsVisible ? (
          <TableCell>
            <DataSetsItemButtons
              id={id}
              archived={archived}
              onEditRecord={onEditRecord}
              onMoveToArchive={onMoveToArchive}
              onDeArchive={onDeArchive}
              onDeleteRecord={onDeleteRecord}
            />
          </TableCell>
        ) : null}
      </TableRow>
      <TableModal
        open={isModalVisible}
        title={t('common.financials', { name })}
        data={formattedProfitCenterLinks}
        onClose={handleToggleModal}
        headers={TABLE_HEADERS_PRODUCTS}
        className={styles.modal}
      />
    </>
  );
}

export default ProductsItem;
